<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Distribution Details</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading distribution details...</p>
      </div>

      <div v-else>
        <!-- Distribution Info -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Distribution Name</label>
              <p class="mb-0 fw-bold">{{ distribution?.distribution_name || '-' }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Destination</label>
              <p class="mb-0 d-flex align-items-center">
                <span
                  class="destination-badge-icon me-2"
                  :class="{ 'destination-badge-icon-img': destinationDisplay.img }"
                  :style="{ backgroundColor: destinationDisplay.img ? 'transparent' : destinationDisplay.color }"
                >
                  <img
                    v-if="destinationDisplay.img"
                    :src="destinationDisplay.img"
                    :alt="destinationDisplay.label"
                    :class="{ 'destination-badge-icon-img-contain': destinationDisplay.imgFit === 'contain' }"
                  />
                  <i v-else-if="destinationDisplay.icon" :class="destinationDisplay.icon"></i>
                  <span v-else class="destination-badge-icon-text">{{ destinationDisplay.iconText }}</span>
                </span>
                {{ destinationDisplay.label }}
              </p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Schedule</label>
              <p class="mb-0"><code>{{ distribution?.schedule || '-' }}</code></p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Entity Types</label>
              <p class="mb-0">{{ distribution?.entity_types || '-' }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Start Date</label>
              <p class="mb-0">{{ formatDate(distribution?.start_date) }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">End Date</label>
              <p class="mb-0">{{ formatDate(distribution?.end_date) }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Lookback Window</label>
              <p class="mb-0">{{ distribution?.lookback_window_days || 1 }} days</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Backfill Period</label>
              <p class="mb-0">{{ distribution?.backfill_period_days || 1 }} days</p>
            </div>
          </div>
        </div>

        <!-- Distribution History -->
        <div class="border-top pt-4">
          <h4 class="mb-3 pb-1">
            <i class="fa fa-history me-2 mb-1"></i>
            Distribution History
          </h4>

          <div v-if="loadingHistory" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <span class="ms-2 text-muted">Loading history...</span>
          </div>

          <div v-else-if="history.length === 0" class="text-center py-3 text-muted">
            No distribution history available.
          </div>

          <div v-else>
            <Datatable v-if="history.length > 0" tableId="distributionHistory" :columns="getHistoryColumns()"
              :formatColumn="historyFormatColumns" uniqueField="id" :data="history" :lengthChange="false" :info="false"
              :showLoading="loadingHistory" :compact="true" :ignoreCache="true" />
          </div>
        </div>

        <div v-if="error" class="alert alert-danger mt-3">
          {{ error }}
        </div>
      </div>
    </div>
    <div class="card-footer text-end">
      <button type="button" class="btn btn-danger" @click="onDeleteClick" :disabled="userRoleRank >= 4">
        <i class="fa fa-trash me-1"></i>
        Delete
      </button>
      <button type="button" class="btn btn-primary ms-2" @click="onEditClick" :disabled="userRoleRank >= 4">
        <i class="fa fa-pen me-1"></i>
        Edit Distribution
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { DateTimeFormat } from '../../utils/commonFunction'
import { getDistributionDestinationDisplay } from '../../utils/field-constants/distributionDestinations'
import Datatable from '../Datatable.vue'

export default {
  name: 'DistributionDetail',
  components: {
    Datatable,
  },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      loadingHistory: false,
      error: null,
      history: [],
      historyColumns: [],
      historyFormatColumns: [
        { index: 4, type: 'datetime' }, // Distribution Date
      ],
    }
  },
  computed: {
    distribution() {
      return this.modalData?.distribution
    },
    audienceName() {
      return this.modalData?.audienceName
    },
    userRoleRank() {
      return this.$store.getters.userRoles.userRoleRank
    },
    destinationDisplay() {
      return getDistributionDestinationDisplay(this.distribution?.destination_name)
    },
  },
  methods: {
    ...mapActions({
      fetchDistributionHistory: 'audience/fetchDistributionHistory',
      deleteDistribution: 'audience/deleteDistribution',
      deleteDistributionHistory: 'audience/deleteDistributionHistory',
      showModal: 'showModal',
      hideModal: 'hideModal',
      addToast: 'addToast',
    }),
    getHistoryColumns() {
      const self = this
      return [
        {
          data: "uuid",
        },
        {
          data: 'matched_count',
          title: 'Matched',
          render: function (data, type) {
            if (type === 'sort') return data
            return self.formatNumber(data)
          }
        },
        {
          data: 'unmatched_count',
          title: 'Unmatched',
          render: function (data, type) {
            if (type === 'sort') return data
            return self.formatNumber(data)
          }
        },
        {
          data: 'distributed_count',
          title: 'Distributed',
          render: function (data, type) {
            if (type === 'sort') return data
            return self.formatNumber(data)
          }
        },
        {
          data: 'distribution_date',
          title: 'Date',
        },
        { 
          data: 'connector_job_id',
          title: 'Job ID',
        },
        { 
          data: 'run_id',
          title: 'Run ID',
        },
      ]
    },
    async loadHistory() {
      if (!this.audienceName || !this.distribution?.distribution_name) return

      this.loadingHistory = true
      try {
        this.history = await this.fetchDistributionHistory({
          audienceName: this.audienceName,
          distributionName: this.distribution.distribution_name,
        })
      } catch (error) {
        console.error('Error loading distribution history:', error)
        // Don't show error - history might not be available
      } finally {
        this.loadingHistory = false
      }
    },
    onEditClick() {
      this.hideModal()
      setTimeout(() => {
        this.showModal({
          component: 'AddDistribution',
          data: {
            audienceName: this.audienceName,
            distribution: this.distribution,
          },
        })
      }, 300)
    },
    async onDeleteClick() {
      const self = this
      this.hideModal()
      setTimeout(() => {
        this.showModal({
          component: 'Confirm',
          data: {
            title: 'Delete Distribution',
            message: `Are you sure you want to delete "${this.distribution?.distribution_name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            confirmClass: 'btn-danger',
            async confirm() {
              try {
                await self.deleteDistribution({
                  audienceName: self.audienceName,
                  distributionName: self.distribution.distribution_name,
                })
                self.addToast({ type: 'success', message: 'Distribution deleted successfully' })
              } catch (error) {
                self.addToast({
                  type: 'danger',
                  message: error.response?.data?.message || error.message || 'Failed to delete distribution',
                })
              }
            },
          },
        })
      }, 300)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return dateString.split('T')[0]
    },
    formatDateTime(dateString) {
      return DateTimeFormat(dateString)
    },
    formatNumber(num) {
      if (num == null) return '-'
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B'
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toLocaleString()
    },
    getStatusBadgeClass(status) {
      const statusLower = (status || '').toLowerCase()
      if (statusLower === 'success' || statusLower === 'completed') {
        return 'badge bg-success'
      } else if (statusLower === 'failed' || statusLower === 'error') {
        return 'badge bg-danger'
      } else if (statusLower === 'running' || statusLower === 'in_progress') {
        return 'badge bg-warning'
      } else if (statusLower === 'pending') {
        return 'badge bg-info'
      }
      return 'badge bg-secondary'
    },
  },
  mounted() {
    this.loadHistory()
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.destination-badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  color: #fff;
  font-size: 0.85rem;
  flex-shrink: 0;
  overflow: hidden;
}

.destination-badge-icon-img {
  border: 1px solid #dee2e6;
}

.destination-badge-icon-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.destination-badge-icon-img img.destination-badge-icon-img-contain {
  padding: 15%;
  box-sizing: border-box;
  object-fit: contain;
}

.destination-badge-icon-text {
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
}
</style>
